import React, { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { ClaimAPI, PolicyBundleAPI } from '../../api';
import { ClaimTermsAcknowledgementProps } from './ClaimTermsAcknowledgement.types';
import Typography from '../../sharedComponents/Typography/Typography';
import styles from './claim-terms-acknowledgement.module.scss';
import BaseCheckboxInput from '../../sharedComponents/base/BaseCheckboxInput/BaseCheckboxInput';
import Button from '../Button/Button';
import { externalLinks } from '../../utils/links';
import { useActions, useAppSelector } from '../../store/hooks';

const ClaimTermsAcknowledgement: React.FC<ClaimTermsAcknowledgementProps> = ({
  openClaimIDs,
}): JSX.Element => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [claimTermsUpdateError, setClaimTermsUpdateError] =
    useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useActions();
  const selectedBundleID = useAppSelector(
    (state) => state.policyBundles.selectedPolicyBundleID,
  );
  const { t } = useTranslation();
  const { termsAndConditionsLinkText, termsAndConditionsLink } = externalLinks;
  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setClaimTermsUpdateError('');
    try {
      const token = await getAccessTokenSilently();
      await ClaimAPI.acceptClaimsTerms(token, openClaimIDs);
      const updatedPolicyBundle = await PolicyBundleAPI.getPolicyBundleByID(
        token,
        selectedBundleID,
      );
      dispatch.setUpdatedPolicyBundle(updatedPolicyBundle);
    } catch (e) {
      setClaimTermsUpdateError(t('error.unable_to_process_req'));
    } finally {
      setIsSubmitting(false);
    }
  };
  const acceptTerms = () => {
    setTermsAccepted((prevState) => !prevState);
  };
  const text = t(termsAndConditionsLinkText);
  const raw = t('sunshine.content.accept_payout_terms.explainer');
  const parts = raw.split(/(<link>.*?<\/link>)/g);
  const label = (
    <>
      {parts.map((part, index) => {
        const match = part.match(/<link>(.*?)<\/link>/);
        if (match) {
          return (
            <a
              key={index}
              href={termsAndConditionsLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {text}
            </a>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
  return (
    <form onSubmit={(e) => submitHandler(e)} className={styles.container}>
      <span className={styles.title}>
        <Typography variant={'label-3'}>
          {t('sunshine.content.accept_payout_terms.explainer.header')}
        </Typography>
      </span>
      <div className={styles.checkbox}>
        <BaseCheckboxInput
          label={label}
          value={'payoutTermsAcceptance'}
          checked={termsAccepted}
          onChangeHandler={acceptTerms}
        ></BaseCheckboxInput>
      </div>
      <Button
        colorVariant={'ocean'}
        clickHandling="submit"
        label={t('generic.action.accept').toUpperCase()}
        disabled={!termsAccepted}
        loading={isSubmitting}
        errorMsg={claimTermsUpdateError}
      />
    </form>
  );
};
export default ClaimTermsAcknowledgement;
